import { KzLanguageOption } from '@shared/kz-ui';

import {
  WalkthroughAppRow,
  WalkthroughDialSegment,
  WalkthroughEngineCard,
  WalkthroughFieldRow,
  WalkthroughFlowNode,
  WalkthroughGlyph,
} from '../models/walkthrough.models';

/**
 * The repeated lists the eight panes draw (ported from koomzodesignsystem
 * `features/auth/data/walkthrough.fixtures.ts`, kz2 F1).
 *
 * ---------------------------------------------------------------------------
 * WHY THESE ARE DATA AND THE REST OF EACH PANE IS MARKUP.
 *
 * The rule applied here: a run of items that differ ONLY in icon and caption is
 * a list, and a list belongs in a typed array where a reviewer can count it.
 * Pane 1's module strip is fourteen chips; writing fourteen near-identical
 * `<div>`s is how the fifteenth ends up with the wrong icon. Everything that
 * differs STRUCTURALLY — pane 7's hub/connector/queue diagram, pane 8's ladder
 * headings, pane 6's dial — stays as markup in the pane that owns it, because
 * turning those into data would mean inventing a schema for one caller.
 *
 * ⚠ These are NOT the catalogue. `MODULE_CHIPS` names fourteen Koomzo modules
 * and `PANE5_TILES` names eight, and neither is derived from — nor may be
 * reconciled with — anything the platform actually knows about installed
 * modules. They are a marketing claim rendered as tiles. Wiring them to real
 * module state would make an unauthenticated pitch look like a live workspace.
 * ---------------------------------------------------------------------------
 *
 * Order is the source's order in every case; it is a designed reading order,
 * not an alphabetisation.
 */

/** The number of panes. */
export const WALKTHROUGH_PANE_COUNT = 8;

/** EN / FR — the pill the shell binds. */
export const WALKTHROUGH_LANGUAGES: readonly KzLanguageOption[] = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

/* ---------------------------------------------------------------- pane 1 --- */

/** The two no-code engines, side by side at `pane-engine` (620px) and up. */
export const ENGINE_CARDS: readonly WalkthroughEngineCard[] = [
  {
    icon: 'clipboard-outline',
    titleKey: 'app.pages.auth.walkthrough.pane1.engine.builder.title',
    bodyKey: 'app.pages.auth.walkthrough.pane1.engine.builder.body',
  },
  {
    icon: 'git-network-outline',
    titleKey: 'app.pages.auth.walkthrough.pane1.engine.orchestrator.title',
    bodyKey: 'app.pages.auth.walkthrough.pane1.engine.orchestrator.body',
  },
];

/**
 * Fourteen module chips in an `auto-fill` grid — the only auto-fill grid in the
 * feature. Note `module.hotel` reads `Hotel & rooms` here and pane 5's tile
 * reads `Hotel & bookings`: one module, two labels, in one source document.
 * Carried across rather than quietly harmonised.
 */
export const MODULE_CHIPS: readonly WalkthroughGlyph[] = [
  { icon: 'card-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.sales' },
  { icon: 'cube-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.stock' },
  { icon: 'receipt-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.invoices' },
  { icon: 'document-text-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.claims' },
  { icon: 'checkmark-done-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.approvals' },
  { icon: 'people-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.customers' },
  { icon: 'bed-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.hotel' },
  { icon: 'restaurant-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.restaurant' },
  { icon: 'cut-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.salon' },
  { icon: 'construct-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.production' },
  { icon: 'time-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.queue' },
  { icon: 'id-card-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.team' },
  { icon: 'tv-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.screens' },
  { icon: 'flash-outline', labelKey: 'app.pages.auth.walkthrough.pane1.module.automations' },
];

/** The three items inside the no-ERP-bloat call-out, 3-up at `pane-cards`. */
export const NO_BLOAT_ITEMS: readonly WalkthroughGlyph[] = [
  { icon: 'apps-outline', labelKey: 'app.pages.auth.walkthrough.pane1.noBloat.apps' },
  { icon: 'options-outline', labelKey: 'app.pages.auth.walkthrough.pane1.noBloat.dial' },
  { icon: 'lock-open-outline', labelKey: 'app.pages.auth.walkthrough.pane1.noBloat.hidden' },
];

/** The four pills across the foot of pane 1. */
export const PITCH_PILLS: readonly WalkthroughGlyph[] = [
  { icon: 'phone-portrait-outline', labelKey: 'app.pages.auth.walkthrough.pane1.pill.phoneFirst' },
  { icon: 'cloud-offline-outline', labelKey: 'app.pages.auth.walkthrough.pane1.pill.offline' },
  { icon: 'swap-horizontal-outline', labelKey: 'app.pages.auth.walkthrough.pane1.pill.localLink' },
  { icon: 'sparkles-outline', labelKey: 'app.pages.auth.walkthrough.pane1.pill.ai' },
];

/* ---------------------------------------------------------------- pane 2 --- */

/**
 * FOUR tiles and, beneath them, one summary node. Pane 5 is EIGHT tiles and no
 * summary. They share two CSS atoms and are not the same composition — this is
 * the closest the eight panes come to a duplicate pair.
 */
export const PANE2_TILES: readonly WalkthroughGlyph[] = [
  { icon: 'card-outline', labelKey: 'app.pages.auth.walkthrough.pane2.tile.sales' },
  { icon: 'cube-outline', labelKey: 'app.pages.auth.walkthrough.pane2.tile.stock' },
  { icon: 'document-text-outline', labelKey: 'app.pages.auth.walkthrough.pane2.tile.applications' },
  { icon: 'bed-outline', labelKey: 'app.pages.auth.walkthrough.pane2.tile.rooms' },
];

/* ---------------------------------------------------------------- pane 3 --- */

/**
 * Five rows of a form-builder canvas. The third carries the focus ring and the
 * fifth is the dashed drop target — which is also the only row the source draws
 * without a drag handle, because you do not grab an empty slot.
 *
 * None of them is draggable, selectable or focusable here.
 */
export const FIELD_ROWS: readonly WalkthroughFieldRow[] = [
  { icon: 'person-outline', labelKey: 'app.pages.auth.walkthrough.pane3.field.name', state: 'plain' },
  { icon: 'call-outline', labelKey: 'app.pages.auth.walkthrough.pane3.field.phone', state: 'plain' },
  { icon: 'cash-outline', labelKey: 'app.pages.auth.walkthrough.pane3.field.amount', state: 'selected' },
  { icon: 'camera-outline', labelKey: 'app.pages.auth.walkthrough.pane3.field.photo', state: 'plain' },
  { icon: 'add-outline', labelKey: 'app.pages.auth.walkthrough.pane3.field.drop', state: 'dropTarget' },
];

/* ---------------------------------------------------------------- pane 4 --- */

/**
 * Four steps joined by three connectors. `tone` picks a badge wash so the chain
 * reads as a sequence — it is not a status vocabulary (see the model's note).
 */
export const FLOW_NODES: readonly WalkthroughFlowNode[] = [
  { icon: 'create-outline', labelKey: 'app.pages.auth.walkthrough.pane4.node.submitted', tone: 'wash' },
  { icon: 'person-circle-outline', labelKey: 'app.pages.auth.walkthrough.pane4.node.approved', tone: 'amber' },
  { icon: 'chatbubble-ellipses-outline', labelKey: 'app.pages.auth.walkthrough.pane4.node.sms', tone: 'green' },
  { icon: 'git-network-outline', labelKey: 'app.pages.auth.walkthrough.pane4.node.updated', tone: 'solid' },
];

/* ---------------------------------------------------------------- pane 5 --- */

/** Eight tiles, no summary node. See `PANE2_TILES` on why these are separate. */
export const PANE5_TILES: readonly WalkthroughGlyph[] = [
  { icon: 'card-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.sales' },
  { icon: 'cube-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.stock' },
  { icon: 'receipt-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.invoices' },
  { icon: 'document-text-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.claims' },
  { icon: 'bed-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.hotel' },
  { icon: 'restaurant-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.restaurant' },
  { icon: 'time-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.queue' },
  { icon: 'id-card-outline', labelKey: 'app.pages.auth.walkthrough.pane5.tile.team' },
];

/* ---------------------------------------------------------------- pane 6 --- */

/**
 * Two installed apps and two that are not. The source draws the not-installed
 * pair with a cross rather than the app's own icon — the point being that you
 * never meet them, so they have no identity on this screen.
 */
export const APP_ROWS: readonly WalkthroughAppRow[] = [
  { icon: 'card-outline', labelKey: 'app.pages.auth.walkthrough.pane6.app.sales', installed: true },
  { icon: 'cube-outline', labelKey: 'app.pages.auth.walkthrough.pane6.app.stock', installed: true },
  { icon: 'close-outline', labelKey: 'app.pages.auth.walkthrough.pane6.app.payroll', installed: false },
  { icon: 'close-outline', labelKey: 'app.pages.auth.walkthrough.pane6.app.manufacturing', installed: false },
];

/**
 * The three segments of the simplicity dial, first one lit.
 *
 * ⚠ THIS IS A PICTURE of the real plan-bar / capability-panel controls, and the
 * source refuses them here by name: they emit a select event; rendering one on
 * an unauthenticated marketing slide would put a live tier picker in front of
 * somebody with no workspace to apply it to. There is no `selected` signal and
 * no output — the `on` flag is a class.
 */
export const DIAL_SEGMENTS: readonly WalkthroughDialSegment[] = [
  { labelKey: 'app.pages.auth.walkthrough.pane6.dial.starter', on: true },
  { labelKey: 'app.pages.auth.walkthrough.pane6.dial.standard', on: false },
  { labelKey: 'app.pages.auth.walkthrough.pane6.dial.full', on: false },
];

/* ---------------------------------------------------------------- pane 7 --- */

/** The three peers under the register hub, `repeat(3, 1fr)` at every width. */
export const LOCAL_DEVICES: readonly WalkthroughGlyph[] = [
  { icon: 'tablet-portrait-outline', labelKey: 'app.pages.auth.walkthrough.pane7.device.tablet' },
  { icon: 'restaurant-outline', labelKey: 'app.pages.auth.walkthrough.pane7.device.kitchen' },
  { icon: 'tv-outline', labelKey: 'app.pages.auth.walkthrough.pane7.device.kiosk' },
];

/** The three clerk chips feeding the queue. Captions only — no icons. */
export const QUEUE_CLERKS: readonly string[] = [
  'app.pages.auth.walkthrough.pane7.clerk.one',
  'app.pages.auth.walkthrough.pane7.clerk.two',
  'app.pages.auth.walkthrough.pane7.clerk.three',
];

/* ---------------------------------------------------------------- pane 8 --- */

/** The Today column — four things a new workspace can do on day one. */
export const LADDER_TODAY: readonly WalkthroughGlyph[] = [
  { icon: 'checkmark-circle', labelKey: 'app.pages.auth.walkthrough.pane8.today.sale' },
  { icon: 'checkmark-circle', labelKey: 'app.pages.auth.walkthrough.pane8.today.customer' },
  { icon: 'checkmark-circle', labelKey: 'app.pages.auth.walkthrough.pane8.today.stock' },
  { icon: 'checkmark-circle', labelKey: 'app.pages.auth.walkthrough.pane8.today.queue' },
];

/**
 * The Later column. In the source these rows are restyled by a DESCENDANT
 * selector on the column, so the rows themselves carry no modifier — the port
 * keeps that shape: the column is `--later`, the rows are the same rows.
 */
export const LADDER_LATER: readonly WalkthroughGlyph[] = [
  { icon: 'lock-closed-outline', labelKey: 'app.pages.auth.walkthrough.pane8.later.approvals' },
  { icon: 'lock-closed-outline', labelKey: 'app.pages.auth.walkthrough.pane8.later.branches' },
  { icon: 'lock-closed-outline', labelKey: 'app.pages.auth.walkthrough.pane8.later.production' },
  { icon: 'lock-closed-outline', labelKey: 'app.pages.auth.walkthrough.pane8.later.location' },
];
